import { z } from "zod";

import { emailSchema } from "@/components/Input/Email/email.schema";
import { createI18nZodParams } from "@/lib/i18n/config/translations";
import { isNonEmptyString } from "@/utils/string";
import { isNumber } from "@/utils/typeGuards";

import type { UploadedFormDataFileInfo } from "../components/fields/FileField";
import type { Attribute, FieldValueType, ValidationRules } from "../types";
import { FieldType, ValidationRuleType } from "../types";

import { getAttributeOptions } from "./option-utils";
import { type ValueLabelObject, isValueLabelObject } from "./value-label-utils";

interface ValidationResult {
  isValid: boolean;
  errorMessage: string | null;
}

/**
 * 必須バリデーションを作成する
 */
const createRequiredValidation = (attribute: Attribute): z.ZodTypeAny => {
  let schema: z.ZodTypeAny = z.any();

  if (attribute.required) {
    schema = schema.refine(
      (val) => {
        if (attribute.type === FieldType.File) {
          return Array.isArray(val) && val.length > 0;
        }
        if (attribute.type === FieldType.MultiSelectCheckbox) {
          return Array.isArray(val) && val.length > 0;
        }
        if (attribute.type === FieldType.Boolean) {
          return val !== undefined && val !== null;
        }
        return val !== undefined && val !== null && val !== "";
      },
      createI18nZodParams("custom.required_error", {
        label: attribute.label,
      }),
    );
  }

  return schema;
};

/**
 * 文字列フィールドのスキーマを作成する
 */
const createStringSchema = (attribute: Attribute) => {
  const baseSchema = createRequiredValidation(attribute);
  return baseSchema.pipe(z.string().optional());
};

/**
 * Emailフィールドのスキーマを作成する
 */
const createEmailSchema = (attribute: Attribute) => {
  const baseSchema = createRequiredValidation(attribute);
  return baseSchema.pipe(
    z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val === "") return true; // 空の値は許可
          return emailSchema.safeParse(val).success;
        },
        createI18nZodParams("custom.email_regexp_error", {
          label: attribute.label,
        }),
      ),
  );
};

/**
 * URLフィールドのスキーマを作成する
 */
const createUrlSchema = (attribute: Attribute) => {
  const baseSchema = createRequiredValidation(attribute);
  return baseSchema.pipe(
    z
      .string()
      .optional()
      .refine(
        (val) => {
          if (!val || val === "") return true;
          try {
            new URL(val);
            return true;
          } catch {
            return false;
          }
        },
        createI18nZodParams("custom.url_regexp_error", {
          label: attribute.label,
        }),
      ),
  );
};

/**
 * 数値フィールドのスキーマを作成する
 */
const createNumberSchema = (
  attribute: Attribute,
  isInteger = false,
): z.ZodTypeAny => {
  const baseSchema = createRequiredValidation(attribute);
  const numberSchema = isInteger ? z.coerce.number().int() : z.coerce.number();
  return baseSchema.pipe(numberSchema.optional());
};

/**
 * ブール値フィールドのスキーマを作成する
 */
const createBooleanSchema = (attribute: Attribute): z.ZodTypeAny => {
  const baseSchema = createRequiredValidation(attribute);
  return baseSchema.pipe(
    z
      .union([z.boolean(), z.string()])
      .optional()
      .transform((val) => {
        if (typeof val === "string") {
          return val.toLowerCase() === "true";
        }
        return val;
      }),
  );
};

/**
 * セレクトフィールドのスキーマを作成する
 * 文字列またはValueLabelObjectのいずれかを受け取る
 */
const createSelectSchema = (attribute: Attribute): z.ZodTypeAny => {
  const baseSchema = createRequiredValidation(attribute);
  return baseSchema.pipe(
    z
      .union([z.string(), z.custom<ValueLabelObject>(isValueLabelObject)])
      .optional()
      .transform((val) => {
        // ValueLabelObjectの場合は値部分を抽出
        if (isValueLabelObject(val)) {
          return val.value;
        }
        return val;
      }),
  );
};

/**
 * ファイルフィールドのスキーマを作成する
 */
const createFileSchema = (attribute: Attribute): z.ZodTypeAny => {
  const fileSetting = attribute.file;
  if (!fileSetting) {
    throw new Error("File setting is not specified for file field");
  }

  // transformを先に行いたいので、必須チェックではなくcreateFileValidationSchemaを先に行う
  const baseSchema = createFileValidationSchema(fileSetting, attribute.label);
  return baseSchema.pipe(createRequiredValidation(attribute));
};

/**
 * ファイルバリデーション用のスキーマを作成する
 */
const createFileValidationSchema = (
  fileSetting: NonNullable<Attribute["file"]>,
  label: string,
) => {
  return z
    .custom<
      | File
      | UploadedFormDataFileInfo
      | (File | UploadedFormDataFileInfo)[]
      | string
      | null
      | undefined
    >(isValidFileValue)
    .transform(transformFileValue)
    .refine(
      (files) =>
        !fileSetting?.maxLength || files.length <= fileSetting.maxLength,
      createI18nZodParams("custom.file.file_max_exceeded", { label }),
    )
    .refine(
      (files) => validateFileSize(files, fileSetting.maxFileSize),
      createI18nZodParams("custom.file.file_size_exceeded", { label }),
    )
    .refine(
      (files) => validateMimeType(files, fileSetting.acceptedMimeTypes),
      createI18nZodParams("custom.file.file_type_not_allowed", { label }),
    )
    .refine(
      (files) => validateFileExtension(files, fileSetting),
      createI18nZodParams("custom.file.file_extension_not_allowed", {
        label,
      }),
    );
};

/**
 * ファイル値が有効かどうかを判定する
 */
const isValidFileValue = (val: unknown): boolean => {
  if (val == null || val === "" || val === "[]") return true;

  if (Array.isArray(val)) {
    return val.every((file) => file instanceof File || "s3Url" in file);
  }

  if (val instanceof File) return true;
  if (typeof val === "object" && "s3Url" in val) return true;

  if (typeof val === "string") {
    try {
      const file = JSON.parse(val);
      return Array.isArray(file)
        ? file.every((f) => "s3Url" in f)
        : "s3Url" in file;
    } catch {
      return false;
    }
  }

  return false;
};

/**
 * ファイル値を配列形式に変換する
 */
const transformFileValue = (
  val: unknown,
): (File | UploadedFormDataFileInfo)[] => {
  if (val == null || val === "" || val === "[]") {
    return [];
  }

  if (Array.isArray(val)) {
    return val;
  }

  if (val instanceof File || (typeof val === "object" && "s3Url" in val)) {
    return [val as File | UploadedFormDataFileInfo];
  }

  if (typeof val === "string") {
    const file = JSON.parse(val);
    return Array.isArray(file) ? file : [file];
  }

  return [];
};

/**
 * ファイルサイズバリデーション
 */
const validateFileSize = (
  files: (File | UploadedFormDataFileInfo)[],
  maxFileSize?: number,
): boolean => {
  if (!maxFileSize) return true;

  return files
    .filter((file): file is File => file instanceof File)
    .every((file) => file.size <= maxFileSize);
};

/**
 * MIMEタイプバリデーション
 */
const validateMimeType = (
  files: (File | UploadedFormDataFileInfo)[],
  acceptedMimeTypes?: string[],
): boolean => {
  if (!acceptedMimeTypes) return true;

  const fileInstances = files.filter(
    (file): file is File => file instanceof File,
  );
  if (fileInstances.length === 0) return true;

  return fileInstances.every((file) => acceptedMimeTypes.includes(file.type));
};

/**
 * ファイル拡張子バリデーション
 */
const validateFileExtension = (
  files: (File | UploadedFormDataFileInfo)[],
  fileSetting: NonNullable<Attribute["file"]>,
): boolean => {
  if (fileSetting.acceptedMimeTypes || !fileSetting.acceptedExtensions)
    return true;

  const fileInstances = files.filter(
    (file): file is File => file instanceof File,
  );
  if (fileInstances.length === 0) return true;

  return fileInstances.every((file) => {
    const extension = file.name.split(".").pop()?.toLowerCase();
    return (
      extension &&
      fileSetting.acceptedExtensions?.some(
        (ext) => ext.toLowerCase() === extension,
      )
    );
  });
};

/**
 * MultiSelectCheckboxフィールドのスキーマを作成する
 */
const createMultiSelectCheckboxSchema = (attribute: Attribute) => {
  // 選択肢がない場合は必須バリデーションをスキップ
  const options = getAttributeOptions(attribute);
  const hasOptions = options.length > 0;

  // 選択肢がない場合は通常のバリデーションを適用し、選択肢がある場合のみ必須バリデーションを適用
  let schema: z.ZodTypeAny = z.any();

  if (attribute.required && hasOptions) {
    schema = schema.refine(
      (val) => Array.isArray(val) && val.length > 0,
      createI18nZodParams("custom.required_error", {
        label: attribute.label,
      }),
    );
  }

  return schema.pipe(z.array(z.string()).optional());
};

/**
 * 属性定義からZodスキーマを作成する
 */
const createZodSchema = (attribute: Attribute): z.ZodTypeAny => {
  if (!attribute) {
    throw new Error("Attribute is required");
  }

  let schema: z.ZodTypeAny;

  switch (attribute.type) {
    case FieldType.Text:
    case FieldType.TextArea:
    case FieldType.ChangeText:
    case FieldType.Password:
    case FieldType.Tel:
    case FieldType.HighlyConfidentialText:
      schema = createStringSchema(attribute);
      break;
    case FieldType.Email:
      schema = createEmailSchema(attribute);
      break;
    case FieldType.Url:
      schema = createUrlSchema(attribute);
      break;
    case FieldType.Integer:
    case FieldType.ChangeInteger:
      schema = createNumberSchema(attribute, true);
      break;
    case FieldType.Decimal:
    case FieldType.ChangeDecimal:
      schema = createNumberSchema(attribute, false);
      break;
    case FieldType.Boolean:
    case FieldType.ChangeBoolean:
      schema = createBooleanSchema(attribute);
      break;
    case FieldType.SingleSelect:
    case FieldType.ChangeSingleSelect:
      schema = createSelectSchema(attribute);
      break;
    case FieldType.MultiSelectCheckbox:
      schema = createMultiSelectCheckboxSchema(attribute);
      break;
    case FieldType.File:
      schema = createFileSchema(attribute);
      break;
    default:
      schema = createStringSchema(attribute);
  }

  // 追加バリデーションルールを適用
  return applyAdditionalValidationRules(schema, attribute);
};

/**
 * 追加のバリデーションルールを適用する
 */
const applyAdditionalValidationRules = (
  schema: z.ZodTypeAny,
  attribute: Attribute,
): z.ZodTypeAny => {
  const stringFieldTypes = [
    FieldType.Text,
    FieldType.TextArea,
    FieldType.ChangeText,
    FieldType.Tel,
    FieldType.Email,
    FieldType.Url,
    FieldType.Password,
    FieldType.HighlyConfidentialText,
  ] as const;

  const numberFieldTypes = [FieldType.Integer, FieldType.Decimal] as const;

  if (
    stringFieldTypes.includes(
      attribute.type as (typeof stringFieldTypes)[number],
    )
  ) {
    return applyStringValidationRules(
      schema as z.ZodString,
      attribute.validationRules || [],
      attribute.label,
      attribute,
    );
  }

  if (
    numberFieldTypes.includes(
      attribute.type as (typeof numberFieldTypes)[number],
    )
  ) {
    return applyNumberValidationRules(
      schema as z.ZodNumber,
      attribute.validationRules || [],
      attribute.label,
      attribute,
    );
  }

  return schema;
};

/**
 * バリデーションルールの型定義
 */
type ValidationRule = {
  rule: ValidationRuleType;
  value: string | number | boolean | Record<string, never>;
};

/**
 * バリデーションルール適用器のI/F
 */
interface ValidationRuleApplier<T extends z.ZodTypeAny> {
  type: ValidationRuleType;
  apply: (
    schema: T,
    rule: ValidationRule,
    attribute: Attribute,
  ) => z.ZodTypeAny;
}

/**
 * 文字列バリデーションルール適用器
 */
const stringValidationAppliers: ValidationRuleApplier<z.ZodString>[] = [
  {
    type: ValidationRuleType.MinLength,
    apply: (schema, rule, attribute) =>
      schema.refine(
        (val) => !isNonEmptyString(val) || val.length >= Number(rule.value),
        createI18nZodParams("custom.min_length_error", {
          label: attribute.label,
          minLength: rule.value,
        }),
      ),
  },
  {
    type: ValidationRuleType.MaxLength,
    apply: (schema, rule, attribute) =>
      schema.refine(
        (val) => !isNonEmptyString(val) || val.length <= Number(rule.value),
        createI18nZodParams("custom.max_length_error", {
          label: attribute.label,
          maxLength: rule.value,
        }),
      ),
  },
  {
    type: ValidationRuleType.Regexp,
    apply: (schema, rule, attribute) => {
      if (typeof rule.value !== "string") return schema;
      const regExp = new RegExp(rule.value);
      return schema.refine(
        (val) => !isNonEmptyString(val) || regExp.test(val),
        createI18nZodParams("custom.pattern_error", {
          label: attribute.label,
          pattern: rule.value,
        }),
      );
    },
  },
];

/**
 * 数値バリデーションルール適用器
 */
const numberValidationAppliers: ValidationRuleApplier<z.ZodNumber>[] = [
  {
    type: ValidationRuleType.MinValue,
    apply: (schema, rule, attribute) =>
      schema.refine(
        (val) =>
          val === undefined ||
          val === null ||
          !isNumber(val) ||
          val >= Number(rule.value),
        createI18nZodParams("custom.min_integer_error", {
          label: attribute.label,
          min: rule.value,
        }),
      ),
  },
  {
    type: ValidationRuleType.MaxValue,
    apply: (schema, rule, attribute) =>
      schema.refine(
        (val) =>
          val === undefined ||
          val === null ||
          !isNumber(val) ||
          val <= Number(rule.value),
        createI18nZodParams("custom.max_integer_error", {
          label: attribute.label,
          max: rule.value,
        }),
      ),
  },
];

/**
 * 汎用バリデーションルール適用関数
 */
const applyValidationRules = <T extends z.ZodTypeAny>(
  schema: T,
  rules: ValidationRules,
  appliers: ValidationRuleApplier<T>[],
  attribute: Attribute,
): z.ZodTypeAny => {
  if (!rules || !Array.isArray(rules)) return schema;

  return rules.reduce((currentSchema, rule) => {
    if (!rule) return currentSchema;

    const applier = appliers.find((a) => a.type === rule.rule);
    if (!applier) return currentSchema;

    return applier.apply(currentSchema as T, rule, attribute);
  }, schema as z.ZodTypeAny);
};

/**
 * 文字列フィールドにバリデーションルールを適用する
 */
const applyStringValidationRules = (
  schema: z.ZodString,
  rules: ValidationRules,
  _label: string,
  attribute?: Attribute,
): z.ZodTypeAny => {
  if (!attribute) return schema;
  return applyValidationRules(
    schema,
    rules,
    stringValidationAppliers,
    attribute,
  );
};

/**
 * 数値フィールドにバリデーションルールを適用する
 */
const applyNumberValidationRules = (
  schema: z.ZodNumber,
  rules: ValidationRule[] | undefined,
  _label: string,
  attribute?: Attribute,
): z.ZodTypeAny => {
  if (!attribute) return schema;
  return applyValidationRules(
    schema,
    rules || [],
    numberValidationAppliers,
    attribute,
  );
};

/**
 * フィールドの値を検証する
 * 単一フィールドの検証を行い、結果を返す
 *
 * @param attribute 検証対象の属性定義
 * @param value 検証する値
 * @returns 検証結果（成功・失敗とエラーメッセージ）
 *
 * @throws {Error} attribute が未定義の場合
 * @throws {Error} スキーマ作成に失敗した場合
 */
export const validateField = (
  attribute: Attribute,
  value: FieldValueType,
): ValidationResult => {
  if (!attribute) {
    throw new Error("Attribute is required for field validation");
  }

  try {
    const schema = createZodSchema(attribute);
    const result = schema.safeParse(value);

    if (result.success) {
      return { isValid: true, errorMessage: null };
    }

    // 最初のエラーメッセージを返す
    const firstError = result.error.errors[0];
    return {
      isValid: false,
      errorMessage: firstError?.message || "Validation failed",
    };
  } catch (error) {
    // スキーマ作成やその他のエラーの場合
    const errorMessage =
      error instanceof Error ? error.message : "Unknown validation error";
    throw new Error(`Field validation failed: ${errorMessage}`);
  }
};
